import React, { useState, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Spinner,
  Modal,
  Button,
  Alert,
} from "react-bootstrap";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import { CheckoutData } from "../hooks/use-save-order";
import { Product } from "../../../store/cart-store";

// Recargo fijo sobre la tarifa de la transportadora (costo inicial del envío).
const SERVIENTREGA_SURCHARGE = 3100;
const INTERRAPIDISIMO_SURCHARGE = 1965;

// Mismo texto que la regla de empaque de order-ms (CARRIER_PACK_RULE_MESSAGE).
const CARRIER_UNAVAILABLE_MESSAGE =
  "Esta transportadora no puede gestionar el envío de un Pack Mayorista junto con packs adicionales. Genera una nueva orden para enviar el pack adicional por separado.";

interface CarrierSelectorProps {
  /** El carrito no lo puede despachar ninguna transportadora. */
  unavailable?: boolean;
  checkoutData: CheckoutData | null;
  destination: { city: string; province: string; address: string };
  cartItems: Product[];
  selectCarrier: (data: {
    carrierId: string;
    shippingCost: number;
  }) => void;
}

const CarrierSelector: React.FC<CarrierSelectorProps> = ({
  unavailable,
  selectCarrier,
  cartItems,
  destination,
}) => {
  const { postData } = useDataFetch();

  const [shippingProvider, setShippingProvider] = useState<
    "interrapidisimo" | "servientrega"
  >();

  const [loading, setLoading] = useState(false);
  const [showNoCoverage, setShowNoCoverage] = useState(false);
  // Regla de empaque de la transportadora (ej. Pack Mayorista con más packs).
  const [ruleMessage, setRuleMessage] = useState<string | null>(null);

  const closeQuoteError = () => {
    setShowNoCoverage(false);
    setRuleMessage(null);
  };

  const failQuote = (response: any) =>
    response?.message === "shipping_rule_violation"
      ? setRuleMessage(response.errors)
      : setShowNoCoverage(true);
  const [servientregaQuote, setServientregaQuote] = useState<any>(null);
  const [interrapidisimoQuote, setInterrapidisimoQuote] = useState<any>(null);

  const servientregaLoaded = useRef(false);
  const interrapidisimoLoaded = useRef(false);

  // El cuerpo de la cotización lo arma order-ms: las medidas, el peso, el
  // valor declarado y la ciudad de origen son reglas de despacho, y tenerlas
  // aquí las duplicaba con las que usa la guía real (y no coincidían).
  // Desde acá sólo viaja lo que el carrito sabe.
  const body = {
    items: cartItems.map((item) => ({
      product_id: item._id,
      quantity: Number(item.quantity || 0),
    })),
    city: destination.city,
    // El departamento desempata municipios homónimos: sin él "Caldas" puede
    // resolverse a la de Boyacá en vez de la de Antioquia.
    province: destination.province,
  };

  /** Normaliza la respuesta de Interrapidísimo (array) al valor más barato. */
  const extractInterrapidisimoTotal = (response: any): number => {
    const list = response?.carriers ?? response;

    if (!Array.isArray(list) || list.length === 0) {
      return NaN;
    }

    const cheapest = list.reduce((min: any, current: any) => {
      const currentValor = Number(current?.Precio?.Valor);
      const minValor = Number(min?.Precio?.Valor);

      if (!Number.isFinite(currentValor)) return min;
      if (!Number.isFinite(minValor)) return current;

      return currentValor < minValor ? current : min;
    });

    return Number(cheapest?.Precio?.Valor) + INTERRAPIDISIMO_SURCHARGE;
  };

  const { mutate: mutateServientrega } = postData(
    "/order/shipping/carriers",
    {},
    {
      onSuccess: (response: any) => {
        setLoading(false);

        const quote = response?.carriers;
        const total = Number(quote?.ValorTotal) + SERVIENTREGA_SURCHARGE;

        if (!quote || !Number.isFinite(total)) {
          if (response?.errors) {
            console.error("Cotización de Servientrega:", response.errors);
          }

          servientregaLoaded.current = false;
          setServientregaQuote(null);
          failQuote(response);
          return;
        }

        setServientregaQuote(quote);

        selectCarrier({
          carrierId: "servientrega",
          shippingCost: total,
        });
      },

      onError: (error: any) => {
        console.error("[servientrega] cotización falló", error);
        setLoading(false);
        servientregaLoaded.current = false;
        setShowNoCoverage(true);
      },
    }
  );

  const { mutate: mutateInterrapidisimo } = postData(
    "/order/shipping/carriers/interrapidisimo",
    {},
    {
      onSuccess: (response: any) => {
        setLoading(false);

        const raw = response?.carriers ?? response;
        const total = extractInterrapidisimoTotal(raw);

        if (!Number.isFinite(total)) {
          console.error(
            "Cotización de Interrapidísimo: respuesta sin valor",
            response
          );

          interrapidisimoLoaded.current = false;
          setInterrapidisimoQuote(null);
          failQuote(response);
          return;
        }

        setInterrapidisimoQuote(raw);

        selectCarrier({
          carrierId: "interrapidisimo",
          shippingCost: total,
        });
      },

      onError: (error: any) => {
        console.error("[interrapidisimo] cotización falló", error);
        setLoading(false);
        interrapidisimoLoaded.current = false;
        setShowNoCoverage(true);
      },
    }
  );

  const loadServientrega = () => {
    if (servientregaLoaded.current) return;

    servientregaLoaded.current = true;

    setLoading(true);

    mutateServientrega(body);
  };

  const loadInterrapidisimo = () => {
    if (interrapidisimoLoaded.current) return;

    interrapidisimoLoaded.current = true;

    setLoading(true);

    mutateInterrapidisimo(body);
  };

  /** Formatea un valor de la cotización sin reventar si no viene. */
  const formatCOP = (value: any) =>
    `$${Number(value ?? 0).toLocaleString("es-CO")}`;

  if (unavailable) {
    return (
      <div>
        <h4 className="fw-semibold mb-4">Selecciona el método de envío</h4>
        <Alert variant="warning" className="mb-0">
          {CARRIER_UNAVAILABLE_MESSAGE}
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <h4 className="fw-semibold mb-4">
        Selecciona el método de envío
      </h4>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card
            className={`h-100 border-2 ${
              shippingProvider === "interrapidisimo"
                ? "border-primary shadow"
                : "border-light"
            }`}
            style={{ cursor: "pointer" }}
            onClick={() => {
              setShippingProvider("interrapidisimo");
              loadInterrapidisimo();
            }}
          >
            <Card.Body className="d-flex">
              <Form.Check
                type="radio"
                checked={shippingProvider === "interrapidisimo"}
                readOnly
                className="me-3 mt-1"
              />

              <div>
                <h5 className="fw-bold">
                  <i className="ri-truck-line me-2 text-primary"></i>
                  Interrapidísimo
                </h5>

                <p className="text-muted mb-0">
                  Conoce el valor del envío antes de finalizar la compra.
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card
            className={`h-100 border-2 ${
              shippingProvider === "servientrega"
                ? "border-primary shadow"
                : "border-light"
            }`}
            style={{ cursor: "pointer" }}
            onClick={() => {
              setShippingProvider("servientrega");
              loadServientrega();
            }}
          >
            <Card.Body className="d-flex">
              <Form.Check
                type="radio"
                checked={shippingProvider === "servientrega"}
                readOnly
                className="me-3 mt-1"
              />

              <div>
                <h5 className="fw-bold">
                  <i className="ri-truck-line me-2 text-success"></i>
                  Servientrega
                </h5>

                <p className="text-muted mb-0">
                  Conoce el valor del envío antes de finalizar la compra.
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              {shippingProvider === "interrapidisimo" && (
                <>
                  {loading && (
                    <div className="text-center py-5">
                      <Spinner animation="border" />

                      <div className="mt-3 text-muted">
                        Consultando tarifa de Interrapidísimo...
                      </div>
                    </div>
                  )}

                  {!loading && interrapidisimoQuote && (
                    <Card className="border bg-light">
                      <Card.Body>
                        <h5 className="fw-semibold mb-4">
                          Cotización del envío
                        </h5>

                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">Total envío</h5>

                          <h4 className="text-primary fw-bold mb-0">
                            {formatCOP(
                              extractInterrapidisimoTotal(
                                interrapidisimoQuote
                              )
                            )}
                          </h4>
                        </div>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}

              {shippingProvider === "servientrega" && (
                <>
                  {loading && (
                    <div className="text-center py-5">
                      <Spinner animation="border" />

                      <div className="mt-3 text-muted">
                        Consultando tarifa de Servientrega...
                      </div>
                    </div>
                  )}

                  {!loading && servientregaQuote && (
                    <Card className="border bg-light">
                      <Card.Body>
                        <h5 className="fw-semibold mb-4">
                          Cotización del envío
                        </h5>

                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">Total envío</h5>

                          <h4 className="text-primary fw-bold mb-0">
                            {formatCOP(
                              servientregaQuote.ValorTotal + SERVIENTREGA_SURCHARGE
                            )}
                          </h4>
                        </div>

                        {servientregaQuote.Informacion && (
                          <Alert variant="info" className="mt-4 mb-0">
                            {servientregaQuote.Informacion}
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h5 className="fw-semibold mb-4">
                Resumen del envío
              </h5>

              {shippingProvider === "interrapidisimo" ? (
                loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : interrapidisimoQuote ? (
                  <>
                    <div className="mb-3">
                      <div className="fw-semibold fs-5">
                        Interrapidísimo
                      </div>

                      <div className="text-muted">
                        Envío calculado
                      </div>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between">
                      <strong>Total</strong>

                      <strong className="text-primary fs-5">
                        {formatCOP(
                          extractInterrapidisimoTotal(
                            interrapidisimoQuote
                          )
                        )}
                      </strong>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted py-4">
                    Selecciona Interrapidísimo para consultar el valor
                    del envío.
                  </div>
                )
              ) : loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : servientregaQuote ? (
                <>
                  <div className="mb-3">
                    <div className="fw-semibold fs-5">
                      Servientrega
                    </div>

                    <div className="text-muted">
                      Envío calculado
                    </div>
                  </div>

                  <hr />

                  <div className="d-flex justify-content-between">
                    <strong>Total</strong>

                    <strong className="text-primary fs-5">
                      {formatCOP(servientregaQuote.ValorTotal + SERVIENTREGA_SURCHARGE)}
                    </strong>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  Selecciona una transportadora para consultar el valor
                  del envío.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showNoCoverage || !!ruleMessage}
        onHide={closeQuoteError}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{ruleMessage ? "Envío no disponible" : "Sin cobertura"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {ruleMessage || "Actualmente no hay cobertura para este destino."}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeQuoteError}
          >
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CarrierSelector;